import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AppShell from '../components/AppShell';
import { useAuth } from '../contexts/AuthContext';
import { FileText, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  seller: 'Personal sale',
  growth_upline: 'Downline commission (Growth)',
  founder_upline: 'Downline commission (Founder)',
};

const monthLabel = (m) => {
  if (!m || m === 'unknown') return 'Undated';
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

export default function CommissionStatements() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMonth, setOpenMonth] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/commissions/statements`, { headers: getAuthHeader() });
        setData(res.data);
        if (res.data.months?.length) setOpenMonth(res.data.months[0].month);
      } catch (e) {
        console.error('Failed to load statements', e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell active="statements" title="Commission Statements">
      <div className="max-w-4xl space-y-6" data-testid="commission-statements-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-playfair text-2xl md:text-3xl text-white flex items-center gap-3">
              <FileText className="w-6 h-6 text-brand-gold" /> Commission Statements
            </h1>
            <p className="text-sm text-gray-400 mt-2">
              Monthly earnings from credited Directory listing sales — every sale behind your totals.
              Membership subscriptions do not generate commission.
            </p>
          </div>
          <div className="bg-brand-surface border border-white/5 rounded-2xl px-6 py-4 text-right">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Lifetime earned</p>
            <p className="font-playfair text-2xl text-brand-gold" data-testid="statements-lifetime-total">
              ${(data?.lifetime_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm py-12 text-center">Loading statements…</p>
        ) : !data?.months?.length ? (
          <div className="bg-brand-surface border border-white/5 rounded-2xl p-12 text-center" data-testid="statements-empty">
            <DollarSign className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <p className="text-white font-medium mb-1">No credited sales yet</p>
            <p className="text-sm text-gray-500">Once your Directory listing sales are verified and credited, they'll appear here month by month.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.months.map((m) => {
              const open = openMonth === m.month;
              return (
                <div key={m.month} className="bg-brand-surface border border-white/5 rounded-2xl overflow-hidden" data-testid={`statement-month-${m.month}`}>
                  <button
                    onClick={() => setOpenMonth(open ? null : m.month)}
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.03] transition-colors"
                    data-testid={`statement-toggle-${m.month}`}
                  >
                    <div className="text-left">
                      <p className="text-white font-medium">{monthLabel(m.month)}</p>
                      <p className="text-xs text-gray-500">{m.count} credited sale{m.count === 1 ? '' : 's'}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-playfair text-xl text-emerald-400">
                        +${m.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                      {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-white/5 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500">
                            <th className="px-6 py-3">Date</th>
                            <th className="px-3 py-3">Type</th>
                            <th className="px-3 py-3">Sale amount</th>
                            <th className="px-3 py-3 text-right pr-6">Your earnings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.sales.map((s) => (
                            <tr key={s.sale_id} className="border-t border-white/5" data-testid={`statement-row-${s.sale_id}`}>
                              <td className="px-6 py-3 text-gray-300 whitespace-nowrap">
                                {s.date ? new Date(s.date).toLocaleDateString() : '—'}
                              </td>
                              <td className="px-3 py-3 text-gray-400">
                                {(s.roles || []).map((r) => ROLE_LABELS[r] || r).join(' + ')}
                                {s.roles?.includes('seller') === false && s.seller_name ? (
                                  <span className="text-gray-600"> · sold by {s.seller_name}</span>
                                ) : null}
                                {s.directory_ref ? <span className="text-gray-600"> · ref {s.directory_ref}</span> : null}
                              </td>
                              <td className="px-3 py-3 text-gray-400">${s.sale_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-3 pr-6 text-right text-emerald-400 font-medium">
                                +${s.my_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
